import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Check, ChevronDown, ChevronUp, Download, Filter, Info, Layers, RefreshCw, Search, Settings, X, Zap, UserCheck, Home, LayoutGrid, CheckCircle2, XCircle, ArrowRightLeft, AlertCircle } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { useToast } from '../../contexts/ToastContext';
import { useData } from '../../contexts/DataContext';
import { useStabilizedGrid, useStabilizedAllocation } from '../../contexts/StabilizedContexts';
import { ConflictData, ConflictSummary, generateConflictSummary, getConflictTypeDefinitions, detectConflicts } from '../../utils/conflictDetection';
import { supabase } from '../../supabase/config';
import { debug } from '../../utils/debug';
import { useAcademicYear } from '../../contexts/AcademicYearContext';

// Conflict resolve/ignore status is persisted per grid in the settings table,
// keyed by the deterministic conflict id (e.g. teacher_double_<code>_<line>), so
// an "Ignored" conflict stays ignored across re-detection and reloads.
type StoredStatus = { status: 'resolved' | 'ignored'; notes?: string };
type StatusMap = Record<string, StoredStatus>;

const conflictStatusKey = (gridId: string) => `conflict_status_${gridId}`;

async function loadConflictStatuses(gridId: string): Promise<StatusMap> {
  try {
    const { data, error } = await supabase
      .from('settings')
      .select('value')
      .eq('key', conflictStatusKey(gridId))
      .maybeSingle();
    if (error || !data?.value) return {};
    const raw = data.value;
    return (typeof raw === 'string' ? JSON.parse(raw) : raw) as StatusMap;
  } catch {
    return {};
  }
}

async function persistConflictStatuses(gridId: string, map: StatusMap): Promise<void> {
  try {
    const { error } = await supabase.from('settings').upsert(
      { key: conflictStatusKey(gridId), value: map, category: 'conflict_status' },
      { onConflict: 'tenant_id,key' }
    );
    if (error) console.error('Failed to persist conflict statuses:', error.message);
  } catch (e) {
    console.error('Failed to persist conflict statuses:', e);
  }
}

// Overlay stored statuses onto freshly detected conflicts (which are always 'open').
function applyStatuses(conflicts: ConflictData[], map: StatusMap): ConflictData[] {
  return conflicts.map(c => {
    const stored = map[c.id];
    if (!stored) return c;
    return {
      ...c,
      status: stored.status,
      details: {
        ...c.details,
        resolution: stored.status === 'ignored' ? 'Ignored' : (c.details?.resolution || 'Resolved'),
        resolutionNotes: stored.notes,
      },
    } as ConflictData;
  });
}

interface ConflictType {
  id: string;
  name: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  count: number;
  active: boolean;
}

const ConflictResolution: React.FC = () => {
  const { addToast } = useToast();
  const { staff } = useData();
  const { activeGrid } = useStabilizedGrid();
  const { workingYear } = useAcademicYear();
  const { assignTeacher, batchAssignTeachers } = useStabilizedAllocation();
  const location = useLocation();
  const navigate = useNavigate();

  // State for real conflict data
  const [conflictTypes, setConflictTypes] = useState<ConflictType[]>([]);
  const [conflicts, setConflicts] = useState<ConflictData[]>([]);
  const [statusMap, setStatusMap] = useState<StatusMap>({});
  const [filteredConflicts, setFilteredConflicts] = useState<ConflictData[]>([]);
  const [conflictSummary, setConflictSummary] = useState<ConflictSummary | null>(null);
  const [selectedConflict, setSelectedConflict] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [expandedConflict, setExpandedConflict] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showResolutionOptions, setShowResolutionOptions] = useState<boolean>(false);
  const [selectedResolution, setSelectedResolution] = useState<string | null>(null);
  const [resolutionNote, setResolutionNote] = useState<string>('');
  const [resolvingConflictId, setResolvingConflictId] = useState<string | null>(null);
  const [resolutionInProgress, setResolutionInProgress] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>('severity');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Get data from navigation state (when coming from GridEditor)
  const [gridId, setGridId] = useState<string | null>(null);
  const [gridName, setGridName] = useState<string>('');
  const [activeSemester, setActiveSemester] = useState<'both' | 'semester1' | 'semester2'>('both');

  // Initialize with real data from navigation state or active grid
  useEffect(() => {
    // Check if we have data from navigation (coming from GridEditor)
    const locationState = location.state as any;
    
    if (locationState?.conflicts && locationState?.gridId) {
      // Use data passed from GridEditor, overlaying any persisted resolve/ignore status.
      setGridId(locationState.gridId);
      setGridName(locationState.gridName || 'Unknown Grid');
      setActiveSemester(locationState.activeSemester || 'both');
      (async () => {
        const map = await loadConflictStatuses(locationState.gridId);
        setStatusMap(map);
        const applied = applyStatuses(locationState.conflicts, map);
        setConflicts(applied);
        const summary = generateConflictSummary(applied);
        setConflictSummary(summary);
        updateConflictTypes(applied, summary);
      })();
    } else if (activeGrid) {
      // Detect conflicts from active grid
      refreshConflicts();
    }
    // Re-run when `staff` loads so teacher names resolve (detection needs staff to
    // turn codes like "JWS" into "Joseph Williams (JWS)").
  }, [location.state, activeGrid, staff]);

  // Helper function to update conflict types based on actual conflicts
  const updateConflictTypes = (conflictData: ConflictData[], summary: ConflictSummary) => {
    const typeDefinitions = getConflictTypeDefinitions();
    const updatedTypes: ConflictType[] = typeDefinitions.map(def => ({
      ...def,
      count: summary.conflictsByType[def.id] || 0,
      active: true
    }));
    
    setConflictTypes(updatedTypes);
  };

  // Real conflict detection function
  const refreshConflicts = async () => {
    if (!activeGrid) return;
    
    setIsLoading(true);
    try {
      // Detect conflicts in current grid, then overlay persisted resolve/ignore status.
      const detectedConflicts = detectConflicts(activeGrid, staff, activeSemester, workingYear);
      const map = await loadConflictStatuses(activeGrid.id);
      setStatusMap(map);
      const applied = applyStatuses(detectedConflicts, map);
      const summary = generateConflictSummary(applied);

      setConflicts(applied);
      setConflictSummary(summary);
      setGridId(activeGrid.id);
      setGridName(activeGrid.name);

      updateConflictTypes(applied, summary);
      
      if (detectedConflicts.length === 0) {
        addToast('No conflicts found!', 'success');
      } else {
        debug('Detected conflicts:', detectedConflicts);
        addToast(`Found ${detectedConflicts.length} conflicts`, 'info');
      }
    } catch (error) {
      console.error('Error detecting conflicts:', error);
      addToast('Error detecting conflicts', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Apply filters to conflicts
  useEffect(() => {
    let result = [...conflicts];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(conflict => 
        conflict.description.toLowerCase().includes(query) || 
        conflict.affectedEntities.some(entity => 
          entity.name.toLowerCase().includes(query) || 
          (entity.code && entity.code.toLowerCase().includes(query))
        ) ||
        conflict.lineName.toLowerCase().includes(query)
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(conflict => conflict.status === statusFilter);
    }
    
    // Apply severity filter
    if (severityFilter.length > 0) {
      result = result.filter(conflict => severityFilter.includes(conflict.severity));
    }
    
    // Apply type filter
    if (typeFilter.length > 0) {
      result = result.filter(conflict => typeFilter.includes(conflict.type));
    }

    // Apply sorting
    result = result.sort((a, b) => {
      if (sortBy === 'severity') {
        const severityOrder = {
          critical: 0,
          high: 1,
          medium: 2,
          low: 3
        };
        const aValue = severityOrder[a.severity];
        const bValue = severityOrder[b.severity];
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      } else if (sortBy === 'line') {
        const aValue = a.lineName || a.lineId;
        const bValue = b.lineName || b.lineId;
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      } else if (sortBy === 'status') {
        const statusOrder = {
          open: 0,
          resolved: 1,
          ignored: 2
        };
        const aValue = statusOrder[a.status];
        const bValue = statusOrder[b.status];
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      } else if (sortBy === 'type') {
        return sortDirection === 'asc' 
          ? a.type.localeCompare(b.type)
          : b.type.localeCompare(a.type);
      }
      return 0;
    });

    setFilteredConflicts(result);
  }, [conflicts, searchQuery, statusFilter, severityFilter, typeFilter, sortBy, sortDirection]);

  // Toggle conflict type active state
  const toggleConflictType = (id: string) => {
    const updatedTypes = conflictTypes.map(type => type.id === id ? {
      ...type,
      active: !type.active
    } : type);
    setConflictTypes(updatedTypes);
    
    // Update type filter based on active conflict types
    const activeTypes = updatedTypes.filter(type => type.active).map(type => type.id);
    setTypeFilter(activeTypes);
  };

  // Toggle conflict expansion
  const toggleConflictExpansion = (id: string) => {
    if (expandedConflict === id) {
      setExpandedConflict(null);
    } else {
      setExpandedConflict(id);
      setSelectedConflict(id);
    }
  };

  // Advisory options change nothing on the timetable — they just point the user
  // at the relevant page. They must NOT mark the conflict resolved.
  const ADVISORY_ACTIONS: Record<string, string> = {
    'Open Staff Directory to review FTE': '/staff/directory',
    'Open Allowances to review': '/staff/allowances',
  };

  // Apply a specific resolution to a conflict
  const applyConflictResolution = async (conflictId: string, resolutionAction: string) => {
    const conflict = conflicts.find(c => c.id === conflictId);
    if (!conflict || !activeGrid) return;

    // Advisory: navigate to the relevant page and leave the conflict OPEN.
    const advisoryPath = ADVISORY_ACTIONS[resolutionAction];
    if (advisoryPath) {
      setShowResolutionOptions(false);
      setResolvingConflictId(null);
      addToast('Opening the relevant page — this conflict stays open until you make a change.', 'info');
      navigate(advisoryPath);
      return;
    }

    setResolutionInProgress(true);
    try {
      if (conflict.type === 'teacher_double_booking') {
        await resolveTeacherDoubleAllocation(conflict, resolutionAction);
      } else if (conflict.type === 'teacher_load_exceeded') {
        await resolveTeacherLoadExceeded(conflict, resolutionAction);
      }

      // Mark conflict as resolved
      const updatedConflicts = conflicts.map(c => c.id === conflictId ? {
        ...c,
        status: 'resolved' as const,
        details: {
          ...c.details,
          resolution: resolutionAction,
          resolutionNotes: `Applied: ${resolutionAction}`,
          resolvedAt: new Date().toISOString()
        }
      } : c);

      setConflicts(updatedConflicts);
      const newSummary = generateConflictSummary(updatedConflicts);
      setConflictSummary(newSummary);
      updateConflictTypes(updatedConflicts, newSummary);

      // Persist so a genuine resolution survives re-detection / reload (Reopen clears it).
      const newMap: StatusMap = {
        ...statusMap,
        [conflictId]: { status: 'resolved', notes: `Applied: ${resolutionAction}` },
      };
      setStatusMap(newMap);
      if (gridId) persistConflictStatuses(gridId, newMap);

      addToast('Conflict resolved successfully!', 'success');
    } catch (error) {
      console.error('Resolution failed:', error);
      addToast('Failed to resolve conflict', 'error');
    } finally {
      setResolutionInProgress(false);
      setResolvingConflictId(null);
      setShowResolutionOptions(false);
    }
  };
  
  // Resolve teacher double allocation conflicts
  const resolveTeacherDoubleAllocation = async (conflict: ConflictData, resolution: string) => {
    const teacherCode = conflict.details.teacherCode;
    const classIds = conflict.details.classIds || [];
    const lineId = conflict.lineId;
    
    if (resolution.includes('Reassign teacher for')) {
      // Extract class name from resolution string
      const className = resolution.replace('Reassign teacher for ', '');
      const targetClass = activeGrid?.classes.find(c => c.className === className);
      
      if (targetClass) {
        // Clear the teacher assignment for this class/line
        await assignTeacher(targetClass.id, lineId, null);
        addToast(`Cleared ${teacherCode} from ${className}`, 'info');
      }
    } else if (resolution === 'Find alternative teacher for one of the classes') {
      // Clear the last class assignment (keep the first one)
      if (classIds.length > 1) {
        const lastClassId = classIds[classIds.length - 1];
        const lastClass = activeGrid?.classes.find(c => c.id === lastClassId);
        if (lastClass) {
          await assignTeacher(lastClassId, lineId, null);
          addToast(`Cleared ${teacherCode} from ${lastClass.className} - please assign different teacher`, 'info');
        }
      }
    }
  };
  
  // Resolve teacher load exceeded conflicts
  const resolveTeacherLoadExceeded = async (conflict: ConflictData, resolution: string) => {
    const teacherCode = conflict.details.teacherCode;
    
    if (resolution === 'Reassign some classes to other teachers') {
      // Find classes assigned to this teacher and clear the most recent ones
      const teacherClasses = activeGrid?.classes.filter(c => 
        c.lineAssignments && Object.values(c.lineAssignments).includes(teacherCode!)
      ) || [];
      
      if (teacherClasses.length > 0) {
        // Clear the assignment from the last assigned class
        const lastClass = teacherClasses[teacherClasses.length - 1];
        const lineId = Object.keys(lastClass.lineAssignments || {}).find(
          key => lastClass.lineAssignments?.[key] === teacherCode
        );
        
        if (lineId) {
          await assignTeacher(lastClass.id, lineId, null);
          addToast(`Cleared ${teacherCode} from ${lastClass.className} to reduce workload`, 'info');
        }
      }
    } else if (resolution === 'Review teacher FTE allocation') {
      // This would typically involve navigating to staff management
      addToast(`Please review ${conflict.details.teacherName || teacherCode}'s FTE in Staff Directory`, 'info');
    } else if (resolution === 'Check for allowances that may reduce available time') {
      addToast(`Please check allowances for ${conflict.details.teacherName || teacherCode} in Staff Allowances`, 'info');
    }
  };
  
  // Legacy resolve function for manual resolution
  const resolveConflict = (id: string, resolution: string, notes: string = '') => {
    const updatedConflicts = conflicts.map(conflict => conflict.id === id ? {
      ...conflict,
      status: 'resolved' as const,
      details: {
        ...conflict.details,
        resolution,
        resolutionNotes: notes
      }
    } : conflict);
    setConflicts(updatedConflicts);
    
    // Update summary
    const newSummary = generateConflictSummary(updatedConflicts);
    setConflictSummary(newSummary);
    updateConflictTypes(updatedConflicts, newSummary);
    
    // Persist so the resolved status survives re-detection / reload.
    const newMap: StatusMap = { ...statusMap, [id]: { status: 'resolved', notes } };
    setStatusMap(newMap);
    if (gridId) persistConflictStatuses(gridId, newMap);

    setSelectedConflict(null);
    setShowResolutionOptions(false);
    setSelectedResolution(null);
    setResolutionNote('');
    addToast('Conflict resolved successfully', 'success');
  };

  // Ignore a conflict
  const ignoreConflict = (id: string, notes: string = '') => {
    const updatedConflicts = conflicts.map(conflict => conflict.id === id ? {
      ...conflict,
      status: 'ignored' as const,
      details: {
        ...conflict.details,
        resolution: 'Ignored',
        resolutionNotes: notes
      }
    } : conflict);
    setConflicts(updatedConflicts);
    
    // Update summary
    const newSummary = generateConflictSummary(updatedConflicts);
    setConflictSummary(newSummary);
    updateConflictTypes(updatedConflicts, newSummary);
    
    // Persist so the ignored status survives re-detection / reload.
    const newMap: StatusMap = { ...statusMap, [id]: { status: 'ignored', notes } };
    setStatusMap(newMap);
    if (gridId) persistConflictStatuses(gridId, newMap);

    setSelectedConflict(null);
    setShowResolutionOptions(false);
    setSelectedResolution(null);
    setResolutionNote('');
    addToast('Conflict marked as ignored', 'info');
  };

  // Reopen a conflict
  const reopenConflict = (id: string) => {
    const updatedConflicts = conflicts.map(conflict => conflict.id === id ? {
      ...conflict,
      status: 'open' as const,
      details: {
        ...conflict.details,
        resolution: undefined,
        resolutionNotes: undefined
      }
    } : conflict);
    setConflicts(updatedConflicts);
    
    // Update summary
    const newSummary = generateConflictSummary(updatedConflicts);
    setConflictSummary(newSummary);
    updateConflictTypes(updatedConflicts, newSummary);

    // Remove the persisted status so re-detection shows it as open again.
    const newMap: StatusMap = { ...statusMap };
    delete newMap[id];
    setStatusMap(newMap);
    if (gridId) persistConflictStatuses(gridId, newMap);

    addToast('Conflict reopened', 'info');
  };


  // Export conflicts report
  const exportConflictsReport = () => {
    if (!conflicts || conflicts.length === 0) {
      addToast('There are no conflicts to export', 'info');
      return;
    }
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Type', 'Severity', 'Description', 'Teacher', 'Status'];
    const rows = conflicts.map((c: any) => [
      c.type, c.severity, c.description || c.title, c.teacherName || c.teacher || '', c.status || 'open'
    ].map(esc).join(','));
    const csv = [header.map(esc).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `timetable-conflicts-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    addToast(`Exported ${conflicts.length} conflicts`, 'success');
  };

  // Get conflict severity color
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-danger-bg text-danger-fg';
      case 'high':
        return 'bg-warn-bg text-warn-fg';
      case 'medium':
        return 'bg-warn-bg text-warn-fg';
      case 'low':
        return 'bg-navy-100 text-navy-800';
      default:
        return 'bg-ink-100 text-ink-900';
    }
  };

  // Get conflict status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-danger-bg text-danger-fg';
      case 'resolved':
        return 'bg-ok-bg text-ok-fg';
      case 'ignored':
        return 'bg-ink-100 text-ink-900';
      default:
        return 'bg-ink-100 text-ink-900';
    }
  };

  // Get entity icon
  const getEntityIcon = (type: string) => {
    switch (type) {
      case 'teacher':
        return <UserCheck className="h-4 w-4 text-navy-500" />;
      case 'room':
        return <Home className="h-4 w-4 text-ok-fg" />;
      case 'class':
        return <LayoutGrid className="h-4 w-4 text-navy-500" />;
      default:
        return <Info className="h-4 w-4 text-ink-500" />;
    }
  };

  // Toggle sort direction
  const toggleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  return (
    <div>
      <PageHeader 
        title="Conflict Resolution" 
        description={gridName ? `Resolving conflicts in ${gridName}` : "Identify and resolve scheduling conflicts in your timetable"}
        actions={
          <Button 
            variant="secondary" 
            onClick={() => navigate('/grid/multi-editor')}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back to Grid Editor
          </Button>
        }
      />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-2">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-ink-400" />
            </div>
            <input 
              type="text" 
              className="block pl-10 pr-3 py-2 text-sm border border-ink-300 rounded-md leading-5 bg-white placeholder-ink-500 focus:outline-none focus:placeholder-ink-400 focus:ring-1 focus:ring-navy-500" 
              placeholder="Search conflicts..." 
              value={searchQuery} 
              onChange={e => setSearchQuery(e.target.value)} 
            />
          </div>
          
          <select 
            className="block pl-3 pr-10 py-2 text-sm border-ink-300 focus:outline-none focus:ring-navy-500 rounded-md" 
            value={statusFilter} 
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="ignored">Ignored</option>
          </select>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button variant="secondary" size="sm" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={refreshConflicts} disabled={isLoading}>
            Refresh
          </Button>
          <Button variant="secondary" size="sm" leftIcon={<Download className="h-4 w-4" />} onClick={exportConflictsReport}>
            Export Report
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-card-title text-ink-900">
                Conflict Types
              </h3>
            </div>
            
            <div className="space-y-3">
              {conflictTypes.map(type => (
                <div 
                  key={type.id} 
                  className={`border rounded-md p-3 cursor-pointer transition-all ${
                    type.active ? 'border-navy-200 bg-navy-50' : 'border-ink-200 bg-ink-50'
                  }`} 
                  onClick={() => toggleConflictType(type.id)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center">
                        <div className={`h-3 w-3 rounded-full mr-2 ${
                          type.severity === 'critical' ? 'bg-danger-solid' : 
                          type.severity === 'high' ? 'bg-warn-solid' : 
                          type.severity === 'medium' ? 'bg-warn-solid' : 'bg-navy-500'
                        }`}></div>
                        <h4 className="text-sm font-medium text-ink-900">
                          {type.name}
                        </h4>
                      </div>
                      <p className="text-xs text-ink-500 mt-1">
                        {type.description}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`font-mono tabular-nums inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(type.severity)}`}>
                        {type.count}
                      </span>
                      <div className={`h-5 w-5 flex items-center justify-center rounded-full ${
                        type.active ? 'bg-navy-100 text-navy-600' : 'bg-ink-100 text-ink-400'
                      }`}>
                        {type.active ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6">
              <div className="bg-ink-50 p-4 rounded-md">
                <h4 className="text-sm font-medium text-ink-700 mb-2">
                  Summary
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-ink-500">Total Conflicts:</span>
                    <span className="font-mono tabular-nums font-medium text-ink-900">
                      {conflictSummary?.totalConflicts || conflicts.length}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-ink-500">Open:</span>
                    <span className="font-mono tabular-nums font-medium text-danger-fg">
                      {conflictSummary?.openConflicts || conflicts.filter(c => c.status === 'open').length}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-ink-500">Critical:</span>
                    <span className="font-mono tabular-nums font-medium text-danger-fg">
                      {conflictSummary?.criticalConflicts || conflicts.filter(c => c.severity === 'critical').length}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-ink-500">Resolved:</span>
                    <span className="font-mono tabular-nums font-medium text-ok-fg">
                      {conflicts.filter(c => c.status === 'resolved').length}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-ink-500">Ignored:</span>
                    <span className="font-mono tabular-nums font-medium text-ink-500">
                      {conflicts.filter(c => c.status === 'ignored').length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-3">
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-card-title text-ink-900">
                Conflicts ({filteredConflicts.length})
              </h3>
              <div className="flex items-center text-sm text-ink-500">
                <span>Sort by:</span>
                <button 
                  className="ml-2 flex items-center text-navy-600 hover:text-navy-800" 
                  onClick={() => toggleSort('severity')}
                >
                  Severity
                  {sortBy === 'severity' && (sortDirection === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />)}
                </button>
                <span className="mx-2">|</span>
                <button 
                  className="flex items-center text-ink-500 hover:text-navy-800" 
                  onClick={() => toggleSort('line')}
                >
                  Line
                  {sortBy === 'line' && (sortDirection === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />)}
                </button>
                <span className="mx-2">|</span>
                <button 
                  className="flex items-center text-ink-500 hover:text-navy-800" 
                  onClick={() => toggleSort('status')}
                >
                  Status
                  {sortBy === 'status' && (sortDirection === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />)}
                </button>
              </div>
            </div>

            {isLoading ? (
              <div className="py-12 text-center">
                <RefreshCw className="h-8 w-8 text-navy-500 mx-auto mb-4 animate-spin" />
                <h4 className="text-card-title text-ink-900 mb-2">
                  Loading Conflicts
                </h4>
                <p className="text-ink-500">
                  Please wait while we analyze your timetable for conflicts...
                </p>
              </div>
            ) : filteredConflicts.length === 0 ? (
              <div className="py-12 text-center">
                <CheckCircle2 className="h-8 w-8 text-ok-fg mx-auto mb-4" />
                <h4 className="text-card-title text-ink-900 mb-2">
                  No Conflicts Found
                </h4>
                <p className="text-ink-500">
                  {searchQuery || statusFilter !== 'all' || severityFilter.length > 0 || typeFilter.length > 0 
                    ? 'No conflicts match your current filters. Try adjusting your search criteria.' 
                    : 'Your timetable is conflict-free. Great job!'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredConflicts.map(conflict => (
                  <div 
                    key={conflict.id} 
                    className={`border rounded-md overflow-hidden transition-all ${
                      expandedConflict === conflict.id ? 'border-navy-300 shadow-md' : 'border-ink-200'
                    } ${selectedConflict === conflict.id ? 'bg-navy-50' : ''}`}
                  >
                    <div 
                      className="p-4 cursor-pointer hover:bg-ink-50" 
                      onClick={() => toggleConflictExpansion(conflict.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start">
                          <div className="mr-3">
                            {conflict.status === 'open' ? 
                              <AlertCircle className={`h-5 w-5 ${
                                conflict.severity === 'critical' ? 'text-danger-fg' : 
                                conflict.severity === 'high' ? 'text-warn-fg' : 
                                conflict.severity === 'medium' ? 'text-warn-fg' : 'text-navy-500'
                              }`} /> : 
                              conflict.status === 'resolved' ? 
                              <CheckCircle2 className="h-5 w-5 text-ok-fg" /> : 
                              <XCircle className="h-5 w-5 text-ink-400" />
                            }
                          </div>
                          <div>
                            <div className="flex items-center">
                              <h4 className="text-sm font-medium text-ink-900">
                                {conflict.description}
                              </h4>
                              <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${getSeverityColor(conflict.severity)}`}>
                                {conflict.severity.charAt(0).toUpperCase() + conflict.severity.slice(1)}
                              </span>
                              <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${getStatusColor(conflict.status)}`}>
                                {conflict.status.charAt(0).toUpperCase() + conflict.status.slice(1)}
                              </span>
                            </div>
                            <div className="flex items-center mt-1 text-xs text-ink-500">
                              <Layers className="h-3 w-3 mr-1" />
                              <span className="font-mono tabular-nums">{conflict.lineName}</span>
                              <span className="mx-1">•</span>
                              <span>{conflict.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          {expandedConflict === conflict.id ? 
                            <ChevronUp className="h-5 w-5 text-ink-400" /> : 
                            <ChevronDown className="h-5 w-5 text-ink-400" />
                          }
                        </div>
                      </div>
                    </div>

                    {expandedConflict === conflict.id && (
                      <div className="px-4 pb-4 border-t border-ink-200 pt-3">
                        <div className="mb-3">
                          <div className="text-xs font-medium text-ink-700 mb-2">
                            Affected Entities:
                          </div>
                          <div className="space-y-1">
                            {conflict.affectedEntities.map((entity, index) => (
                              <div key={index} className="flex items-center text-sm text-ink-500">
                                {getEntityIcon(entity.type)}
                                <span className="ml-2">
                                  {entity.name} {entity.code ? `(${entity.code})` : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {conflict.status === 'resolved' && (
                          <div className="mb-3 bg-ok-bg p-3 rounded-md">
                            <div className="flex items-start">
                              <Check className="h-5 w-5 text-ok-fg mr-2 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-ok-fg">
                                  Resolution Applied
                                </div>
                                <div className="text-xs text-ok-fg mt-1">
                                  {conflict.details.resolution || 'Resolved'}
                                </div>
                                {conflict.details.resolutionNotes && (
                                  <div className="text-xs text-ok-fg mt-2 italic">
                                    {conflict.details.resolutionNotes}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {conflict.status === 'ignored' && (
                          <div className="mb-3 bg-ink-50 p-3 rounded-md">
                            <div className="flex items-start">
                              <Info className="h-5 w-5 text-ink-500 mr-2 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-ink-900">
                                  Conflict Ignored
                                </div>
                                {conflict.details.resolutionNotes && (
                                  <div className="text-xs text-ink-700 mt-1 italic">
                                    {conflict.details.resolutionNotes}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Resolution Options */}
                        {resolvingConflictId === conflict.id && (
                          <div className="mt-4 bg-navy-50 border border-navy-200 rounded-md p-4">
                            <div className="text-sm font-medium text-navy-800 mb-3">
                              Choose Resolution Action:
                            </div>
                            <div className="space-y-2">
                              {conflict.resolutionOptions.map((option, index) => (
                                <button
                                  key={index}
                                  className="w-full text-left px-3 py-2 text-sm bg-white border border-navy-200 rounded hover:bg-navy-50 hover:border-navy-300 transition-colors"
                                  onClick={() => applyConflictResolution(conflict.id, option)}
                                  disabled={resolutionInProgress}
                                >
                                  <div className="flex items-start">
                                    <ArrowRightLeft className="h-4 w-4 text-navy-500 mr-2 mt-0.5 flex-shrink-0" />
                                    <span className="text-ink-700">{option}</span>
                                  </div>
                                </button>
                              ))}
                            </div>
                            <div className="flex justify-end space-x-2 mt-3 pt-3 border-t border-navy-200">
                              <Button 
                                variant="secondary" 
                                size="sm" 
                                onClick={() => setResolvingConflictId(null)}
                                disabled={resolutionInProgress}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex justify-end space-x-2">
                          {conflict.status === 'open' ? (
                            <>
                              <Button 
                                variant="secondary" 
                                size="sm" 
                                leftIcon={<X className="h-4 w-4" />} 
                                onClick={() => ignoreConflict(conflict.id)}
                                disabled={resolutionInProgress}
                              >
                                Ignore
                              </Button>
                              <Button 
                                variant="primary" 
                                size="sm" 
                                leftIcon={resolutionInProgress && resolvingConflictId === conflict.id ? 
                                  <RefreshCw className="h-4 w-4 animate-spin" /> : 
                                  <Check className="h-4 w-4" />
                                } 
                                onClick={() => setResolvingConflictId(conflict.id)}
                                disabled={resolutionInProgress || resolvingConflictId !== null}
                              >
                                {resolutionInProgress && resolvingConflictId === conflict.id ? 'Resolving...' : 'Resolve'}
                              </Button>
                            </>
                          ) : (
                            <Button 
                              variant="secondary" 
                              size="sm" 
                              leftIcon={<ArrowRightLeft className="h-4 w-4" />} 
                              onClick={() => reopenConflict(conflict.id)}
                            >
                              Reopen
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ConflictResolution;